var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { selectTool } from '../helper/helper';
import { saveState } from '../helper/stateManagerHelper';
import customEventHandler from '../helper/customEvents';
import { getImageSrc } from '../helper/imageBlobCache';
var TrayCounter = function (_a) {
    var _b, _c;
    var id = _a.id, data = _a.data;
    var center = data.center, dimensions = data.dimensions, _d = data.rotation, rotation = _d === void 0 ? 0 : _d, _e = data.scale, scale = _e === void 0 ? 1 : _e, customImage = data.customImage, customImageBack = data.customImageBack, _f = data.showImage, showImage = _f === void 0 ? 'front' : _f, lock = data.lock;
    var _g = useContext(DataContext), selectedTool = _g.selectedTool, setSelectedTool = _g.setSelectedTool, manipulative = _g.manipulative, setManipulative = _g.setManipulative, panningEnable = _g.panningEnable, setContextMenu = _g.setContextMenu;
    var _h = globalStore.useStoreState(function (s) { return s.playerStore; }), canvasHeight = _h.canvasHeight, canvasWidth = _h.canvasWidth, zoomLevel = _h.zoomLevel;
    var wrapperRef = useRef(null);
    var longPressRef = useRef(null);
    var dragStartRef = useRef(null);
    var _j = useState(false), isDragging = _j[0], setIsDragging = _j[1];
    var isSelected = selectedTool.includes(id);
    var isDraggable = !(lock === null || lock === void 0 ? void 0 : lock.positioning) && !(lock === null || lock === void 0 ? void 0 : lock.editing) && !panningEnable;
    var imageSrcKey = customImageBack && showImage === 'back' ? customImageBack : customImage;
    var imageSrc = getImageSrc(imageSrcKey);
    var openContextMenu = function (x, y) {
        if (!selectedTool.includes(id))
            setSelectedTool([id]);
        setContextMenu({ x: x, y: y, manipulativeId: id });
    };
    var handleMouseDown = function (e) {
        if (panningEnable) {
            e.stopPropagation();
            return;
        }
        e.stopPropagation();
        setSelectedTool(selectTool(e, id, selectedTool, false));
        if (!isDraggable)
            return;
        e.preventDefault();
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        setIsDragging(true);
        dragStartRef.current = {
            clientX: clientX,
            clientY: clientY,
            startCenter: { x: center.x, y: center.y },
            hasMoved: false,
            completeData: JSON.parse(JSON.stringify(data)),
        };
    };
    var handleTouchStart = function (e) {
        var cx = e.touches[0].clientX;
        var cy = e.touches[0].clientY;
        longPressRef.current = setTimeout(function () {
            longPressRef.current = null;
            setIsDragging(false);
            dragStartRef.current = null;
            openContextMenu(cx, cy);
        }, 600);
        handleMouseDown(e);
    };
    useEffect(function () { return function () {
        if (longPressRef.current)
            clearTimeout(longPressRef.current);
    }; }, []);
    useEffect(function () {
        if (!isDragging || !dragStartRef.current)
            return;
        var cancelLongPress = function () {
            if (longPressRef.current) {
                clearTimeout(longPressRef.current);
                longPressRef.current = null;
            }
        };
        var onMove = function (e) {
            cancelLongPress();
            if (!dragStartRef.current)
                return;
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            var dxClient = clientX - dragStartRef.current.clientX;
            var dyClient = clientY - dragStartRef.current.clientY;
            if (!dragStartRef.current.hasMoved &&
                Math.abs(dxClient) < 5 &&
                Math.abs(dyClient) < 5) {
                return;
            }
            dragStartRef.current.hasMoved = true;
            var dx = dxClient / zoomLevel;
            var dy = dyClient / zoomLevel;
            var nx = Math.max(0, Math.min(canvasWidth, dragStartRef.current.startCenter.x + dx));
            var ny = Math.max(0, Math.min(canvasHeight, dragStartRef.current.startCenter.y + dy));
            setManipulative(function (prev) {
                return prev.map(function (m) {
                    return m.data.id === id ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: { x: nx, y: ny } }) }) : m;
                });
            });
        };
        var onUp = function () {
            var _a, _b, _c, _d, _e;
            cancelLongPress();
            var moved = (_b = (_a = dragStartRef.current) === null || _a === void 0 ? void 0 : _a.hasMoved) !== null && _b !== void 0 ? _b : false;
            var oldComplete = (_c = dragStartRef.current) === null || _c === void 0 ? void 0 : _c.completeData;
            setIsDragging(false);
            dragStartRef.current = null;
            if (!moved || !oldComplete)
                return;
            var current = (_d = manipulative.find(function (m) { return m.data.id === id; })) === null || _d === void 0 ? void 0 : _d.data;
            if (!current)
                return;
            saveState({
                type: id,
                id: (_e = id.split('_')[1]) !== null && _e !== void 0 ? _e : id,
                propertyName: 'completeData',
                old: JSON.stringify(oldComplete),
                new: JSON.stringify(current),
            });
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
        return function () {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
    }, [isDragging, id, zoomLevel, canvasWidth, canvasHeight, manipulative, setManipulative]);
    // Listen for right-panel + context-menu actions (flip / rotateClockwise / rotateAnticlockwise)
    useEffect(function () {
        if (!isSelected)
            return;
        var listener = function (event) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            if (!selectedTool.includes(id))
                return;
            switch (event.id) {
                case 'flip': {
                    if (!customImageBack)
                        return;
                    var next_1 = showImage === 'back' ? 'front' : 'back';
                    saveState({
                        type: id,
                        id: (_a = id.split('_')[1]) !== null && _a !== void 0 ? _a : id,
                        propertyName: 'showImage',
                        old: JSON.stringify(showImage),
                        new: JSON.stringify(next_1),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (m) {
                            return m.data.id === id ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { showImage: next_1 }) }) : m;
                        });
                    });
                    break;
                }
                case 'rotateClockwise':
                case 'rotateAnticlockwise': {
                    var dir = event.id === 'rotateClockwise' ? 1 : -1;
                    var next_2 = (rotation + dir + 360) % 360;
                    saveState({
                        type: id,
                        id: (_b = id.split('_')[1]) !== null && _b !== void 0 ? _b : id,
                        propertyName: 'rotation',
                        old: JSON.stringify(rotation),
                        new: JSON.stringify(next_2),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (m) {
                            return m.data.id === id ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { rotation: next_2 }) }) : m;
                        });
                    });
                    break;
                }
                case 'scaleMinus':
                case 'scalePlus': {
                    // Scale step matches the in-frame Counter (0.1 per tap, floor 0.5).
                    // Recompute dimensions off `defaultDimensions` so the visual size
                    // stays anchored to the natural image size on every tap (avoids
                    // compounding rounding drift).
                    var isPlus = event.id === 'scalePlus';
                    var nextScale_1 = isPlus
                        ? parseFloat((scale + 0.1).toFixed(1))
                        : Math.max(0.5, parseFloat((scale - 0.1).toFixed(1)));
                    if (nextScale_1 === scale)
                        break;
                    var defaultW_1 = (_e = (_d = (_c = data.defaultDimensions) === null || _c === void 0 ? void 0 : _c.width) !== null && _d !== void 0 ? _d : dimensions === null || dimensions === void 0 ? void 0 : dimensions.width) !== null && _e !== void 0 ? _e : 60;
                    var defaultH_1 = (_h = (_g = (_f = data.defaultDimensions) === null || _f === void 0 ? void 0 : _f.height) !== null && _g !== void 0 ? _g : dimensions === null || dimensions === void 0 ? void 0 : dimensions.height) !== null && _h !== void 0 ? _h : 60;
                    saveState({
                        type: id,
                        id: (_j = id.split('_')[1]) !== null && _j !== void 0 ? _j : id,
                        propertyName: 'scale',
                        old: JSON.stringify(scale),
                        new: JSON.stringify(nextScale_1),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (m) {
                            return m.data.id === id
                                ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { scale: nextScale_1, dimensions: {
                                            width: defaultW_1 * nextScale_1,
                                            height: defaultH_1 * nextScale_1,
                                        } }) }) : m;
                        });
                    });
                    break;
                }
            }
        };
        customEventHandler.addEventListener('bottomPanel', listener);
        return function () { return customEventHandler.removeEventListener('bottomPanel', listener); };
    }, [
        isSelected,
        selectedTool,
        id,
        customImageBack,
        showImage,
        rotation,
        scale,
        data.defaultDimensions,
        dimensions,
        setManipulative,
    ]);
    if (!customImage)
        return null;
    var w = (_b = dimensions === null || dimensions === void 0 ? void 0 : dimensions.width) !== null && _b !== void 0 ? _b : 60;
    var h = (_c = dimensions === null || dimensions === void 0 ? void 0 : dimensions.height) !== null && _c !== void 0 ? _c : 60;
    return (_jsx("div", { ref: wrapperRef, "data-id": id, "data-manipulative-id": id, role: "img", "aria-label": t('text.counterLabel', { shape: 'image', color: '' }), style: {
            position: 'absolute',
            left: center.x - w / 2,
            top: center.y - h / 2,
            width: w,
            height: h,
            transform: "rotate(".concat(rotation, "deg)"),
            cursor: isDraggable ? (isDragging ? 'grabbing' : 'grab') : 'default',
            pointerEvents: 'auto',
            userSelect: 'none',
        }, onMouseDown: handleMouseDown, onTouchStart: handleTouchStart, onContextMenu: function (e) {
            e.preventDefault();
            e.stopPropagation();
            openContextMenu(e.clientX, e.clientY);
        }, onClick: function (e) { return e.stopPropagation(); }, children: _jsx("img", { src: imageSrc, alt: "", draggable: false, style: { width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' } }) }));
};
export default TrayCounter;
